import React from 'react';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

const socialLinks = [
  {
    icon: <GitHubIcon sx={{ fontSize: '1.1rem' }} />,
    label: 'GitHub',
    href: 'https://github.com/saad9797',
    color: '#f8fafc',
  },
  {
    icon: <LinkedInIcon sx={{ fontSize: '1.1rem' }} />,
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/syed-saad-waqar-97167a258/',
    color: '#0ea5e9',
  },
  {
    icon: <EmailIcon sx={{ fontSize: '1.1rem' }} />,
    label: 'Email',
    href: 'mailto:waqarsaad76@gmail.com',
    color: '#6366f1',
  },
];

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        mt: 'auto',
        overflow: 'hidden',
        borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: (theme) =>
          `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Subtle top glow line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '60%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.5), rgba(6,182,212,0.4), transparent)',
        }}
      />

      {/* Background orb */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '-40%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 500,
          height: 200,
          background: 'radial-gradient(ellipse, rgba(99,102,241,0.06) 0%, transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* ── CTA Strip ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              textAlign: 'center',
              pt: { xs: 6, md: 8 },
              pb: { xs: 5, md: 6 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.72rem', md: '0.75rem' },
                fontWeight: 700,
                letterSpacing: '0.2em',
                color: 'primary.main',
                mb: 1.5,
                textTransform: 'uppercase',
              }}
            >
              Let's Build Something
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #f8fafc 0%, #94a3b8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 1.5,
              }}
            >
              Open to Opportunities
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', md: '0.9375rem' },
                maxWidth: 460,
                mx: 'auto',
                lineHeight: 1.7,
                mb: 3.5,
              }}
            >
              Associate Developer · Full Stack · Backend — Karachi, Pakistan.
              Available for full-time roles.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  component="a"
                  href="mailto:waqarsaad76@gmail.com"
                  data-cursor="hover"
                  variant="contained"
                  sx={{
                    px: 3,
                    py: 1.25,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    boxShadow: '0 6px 20px rgba(99,102,241,0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)',
                      boxShadow: '0 8px 24px rgba(99,102,241,0.5)',
                    },
                  }}
                >
                  Hire Me
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  component="a"
                  href="/Resume/Resume - Syed Saad Waqar.pdf"
                  data-cursor="hover"
                  download
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    px: 3,
                    py: 1.25,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    borderRadius: '12px',
                    borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                    color: 'text.primary',
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                      background: (theme) => alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  Download Resume
                </Button>
              </motion.div>
            </Box>
          </Box>
        </motion.div>

        <Divider sx={{ borderColor: (theme) => alpha(theme.palette.divider, 0.08) }} />

        {/* ── Bottom bar ── */}
        <Box
          sx={{
            py: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 3px 10px rgba(99,102,241,0.35)',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.65rem', color: '#fff', letterSpacing: '-0.5px' }}>
                SSW
              </Typography>
            </Box>
            <Typography sx={{ color: 'text.disabled', fontSize: '0.8rem', fontWeight: 500 }}>
              © {new Date().getFullYear()} Syed Saad Waqar
            </Typography>
          </Box>

          {/* Social icons */}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {socialLinks.map((link) => (
              <motion.div key={link.label} whileHover={{ y: -2 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  component="a"
                  href={link.href}
                  data-cursor="hover"
                  target={link.label !== 'Email' ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  size="small"
                  sx={{
                    width: 34,
                    height: 34,
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    borderRadius: '10px',
                    color: 'text.disabled',
                    backdropFilter: 'blur(8px)',
                    background: (theme) => alpha(theme.palette.background.paper, 0.4),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: link.color,
                      borderColor: alpha(link.color, 0.35),
                      background: alpha(link.color, 0.08),
                      boxShadow: `0 4px 14px ${alpha(link.color, 0.2)}`,
                    },
                  }}
                >
                  {link.icon}
                </IconButton>
              </motion.div>
            ))}
          </Box>

          {/* Back to top */}
          <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.9 }}>
            <IconButton
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              data-cursor="hover"
              aria-label="Back to top"
              size="small"
              sx={{
                width: 34,
                height: 34,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                borderRadius: '10px',
                color: 'text.disabled',
                backdropFilter: 'blur(8px)',
                background: (theme) => alpha(theme.palette.background.paper, 0.4),
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: '#6366f1',
                  borderColor: 'rgba(99,102,241,0.35)',
                  background: 'rgba(99,102,241,0.08)',
                },
              }}
            >
              <ArrowUpwardIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;

